export const PERIOD_OPTIONS = [7, 30, 90, 365];
export const DEFAULT_PERIOD_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000; // eslint-disable-line no-unused-vars
const MONTH_AVG_DAYS = 365.2425 / 12;

export const MUSCLE_BUCKETS = [
  { id: "chest", label: "Chest" },
  { id: "back", label: "Back" },
  { id: "shoulders", label: "Shoulders" },
  { id: "arms", label: "Arms" },
  { id: "legs", label: "Legs" },
  { id: "core", label: "Core" }
];

const MUSCLE_ALIASES = new Map([
  ["chest", "chest"], ["pectorals", "chest"], ["upper_chest", "chest"], ["lower_chest", "chest"],
  ["back", "back"], ["upper_back", "back"], ["lower_back", "back"], ["lats", "back"],
  ["latissimus_dorsi", "back"], ["traps", "back"], ["trapezius", "back"], ["rhomboids", "back"],
  ["shoulders", "shoulders"], ["shoulder", "shoulders"], ["delts", "shoulders"],
  ["deltoids", "shoulders"], ["front_delts", "shoulders"], ["side_delts", "shoulders"], ["rear_delts", "shoulders"],
  ["neck", "back"],
  ["biceps", "arms"], ["triceps", "arms"], ["forearms", "arms"], ["arms", "arms"],
  ["legs", "legs"], ["quads", "legs"], ["quadriceps", "legs"], ["hamstrings", "legs"],
  ["glutes", "legs"], ["calves", "legs"], ["adductors", "legs"], ["abductors", "legs"],
  ["core", "core"], ["abs", "core"], ["abdominals", "core"], ["obliques", "core"]
]);

// Maps normalized Hevy muscle name → react-body-highlighter muscle string(s)
// Used for granular per-muscle coloring on the body map.
// Keys not present here fall back to BUCKET_TO_LIBRARY via MUSCLE_ALIASES.
const MUSCLE_TO_LIBRARY = new Map([
  ["chest", ["chest"]], ["pectorals", ["chest"]], ["upper_chest", ["chest"]], ["lower_chest", ["chest"]],
  ["lats", ["upper-back"]], ["latissimus_dorsi", ["upper-back"]], ["upper_back", ["upper-back"]], ["rhomboids", ["upper-back"]],
  ["traps", ["trapezius"]], ["trapezius", ["trapezius"]],
  ["lower_back", ["lower-back"]],
  ["front_delts", ["front-deltoids"]],
  ["rear_delts", ["back-deltoids"]],
  ["biceps", ["biceps"]], ["triceps", ["triceps"]], ["forearms", ["forearm"]],
  ["quadriceps", ["quadriceps"]], ["quads", ["quadriceps"]],
  ["hamstrings", ["hamstring"]],
  ["glutes", ["gluteal"]],
  ["calves", ["calves"]],
  ["adductors", ["adductor"]], ["abductors", ["abductors"]],
  ["abs", ["abs"]], ["abdominals", ["abs"]],
  ["obliques", ["obliques"]],
  ["neck", ["neck"]],
]);

// Fallback: when a Hevy muscle is generic (maps to bucket but not MUSCLE_TO_LIBRARY),
// spread the setCount across all library muscles in that bucket.
const BUCKET_TO_LIBRARY = {
  chest:     ["chest"],
  back:      ["upper-back", "lower-back", "trapezius"],
  shoulders: ["front-deltoids", "back-deltoids"],
  arms:      ["biceps", "triceps", "forearm"],
  legs:      ["quadriceps", "hamstring", "gluteal", "calves", "abductors", "adductor"],
  core:      ["abs", "obliques"],
};

export function buildPeriodAnalytics(workoutsInput = [], periodDays = DEFAULT_PERIOD_DAYS, nowInput = new Date()) {
  const safePeriodDays = PERIOD_OPTIONS.includes(Number(periodDays)) ? Number(periodDays) : DEFAULT_PERIOD_DAYS;
  const workouts = normalizeWorkoutList(workoutsInput);
  const periods = getRollingPeriods(safePeriodDays, nowInput);
  const currentWorkouts = filterWorkoutsByRange(workouts, periods.current.start, periods.current.end);
  const previousWorkouts = filterWorkoutsByRange(workouts, periods.previous.start, periods.previous.end);
  const current = summarizePeriod(currentWorkouts, safePeriodDays, periods.current);
  const previous = summarizePeriod(previousWorkouts, safePeriodDays, periods.previous);

  // Personal records are all-time (across every imported workout, ignoring the
  // period filter) and sourced from Hevy's own per-set PR markers.
  const exerciseRecords = buildExerciseRecords(workouts);
  const exerciseTrends = buildExerciseTrends(workouts);
  for (const item of current.exerciseFrequency) {
    item.records = exerciseRecords.get(item.name) || {};
    item.trend = exerciseTrends.get(item.name) || null;
  }

  return {
    periodDays: safePeriodDays,
    generatedAt: periods.generatedAt.toISOString(),
    periods: serializePeriods(periods),
    current,
    previous,
    deltas: buildDeltas(current, previous),
    weeklySeries: buildWeeklySeries(currentWorkouts, periods.current.start, periods.current.end),
    monthlySeries: buildMonthlySeries(currentWorkouts, periods.current.start, periods.current.end),
    muscleBuckets: current.muscleBuckets,
    muscleLibraryCounts: current.muscleLibraryCounts,
    exerciseFrequency: current.exerciseFrequency,
    equipmentBreakdown: current.equipmentBreakdown,
    // Like personal records, consistency ignores the period filter — a streak is
    // only meaningful across the whole imported history.
    consistency: buildConsistency(workouts, periods.generatedAt)
  };
}

export function mapMuscleToBucket(rawName) {
  return MUSCLE_ALIASES.get(normalizeKey(rawName)) || "";
}

export function calculateSetVolume(set) {
  const weight = Number(set?.weight);
  const reps = Number(set?.reps);
  if (!Number.isFinite(weight) || !Number.isFinite(reps)) return 0;
  return weight * reps;
}

function betterRecord(current, candidate) {
  return !current || candidate.value > current.value ? candidate : current;
}

// Hevy flags PR sets with a `personalRecords: [{ type, value }]` array, computed
// against the athlete's full history. We keep the max value per exercise+type.
const HEVY_RECORD_TYPES = ["best_weight", "best_1rm", "best_volume"];

function buildExerciseRecords(workouts) {
  const byName = new Map();
  for (const workout of workouts) {
    const ref = { workoutId: workout.id, shortId: workout.shortId, date: workout.startTime };
    for (const exercise of workout.exercises || []) {
      const name = String(exercise.title || "Unknown exercise").trim() || "Unknown exercise";
      const records = byName.get(name) || {};
      for (const set of Array.isArray(exercise.sets) ? exercise.sets : []) {
        const prs = Array.isArray(set?.personalRecords) ? set.personalRecords : [];
        for (const pr of prs) {
          if (!pr || !HEVY_RECORD_TYPES.includes(pr.type)) continue;
          const value = Number(pr.value);
          if (!Number.isFinite(value)) continue;
          records[pr.type] = betterRecord(records[pr.type], { value, weight: Number(set.weight), reps: Number(set.reps), ...ref });
        }
      }
      byName.set(name, records);
    }
  }
  return byName;
}

function normalizeWorkoutList(workoutsInput) {
  return (Array.isArray(workoutsInput) ? workoutsInput : [])
    .filter(Boolean)
    .map((w) => ({ ...w, _date: parseDate(w.startTime) }))
    .filter((w) => w._date);
}

function getRollingPeriods(periodDays, nowInput) {
  const now = parseDate(nowInput) || new Date();
  const currentEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  const currentStart = addUtcDays(currentEnd, -periodDays);
  const previousStart = addUtcDays(currentStart, -periodDays);
  return { generatedAt: now, current: { start: currentStart, end: currentEnd }, previous: { start: previousStart, end: currentStart } };
}

function serializePeriods(periods) {
  return { current: serializePeriod(periods.current), previous: serializePeriod(periods.previous) };
}

function serializePeriod(period) {
  return { start: toIsoDate(period.start), endExclusive: toIsoDate(period.end), endInclusive: toIsoDate(addUtcDays(period.end, -1)) };
}

function filterWorkoutsByRange(workouts, start, end) {
  return workouts.filter((w) => w._date >= start && w._date < end);
}

function summarizePeriod(workouts, periodDays, period) {
  const totals = { workoutCount: workouts.length, totalDurationSeconds: 0, totalVolumeKg: 0, volumeWorkoutCount: 0, totalSets: 0, prSetCount: 0,
    totalCalories: 0, caloriesCount: 0, heartRateSum: 0, heartRateCount: 0, maxHeartRate: 0, maxHeartRateCount: 0 };
  const muscleTotals = new Map(MUSCLE_BUCKETS.map((b) => [b.id, 0]));
  const libraryMuscleTotals = new Map();
  const exercises = new Map();
  const equipmentTotals = new Map();

  for (const workout of workouts) {
    totals.totalDurationSeconds += numberOrZero(workout.durationSeconds);

    // Hevy sends 0 rather than null for a workout logged without a heart-rate
    // strap, so counting those as tracked drags the average toward zero.
    const bio = workout.biometrics;
    const calories = numberOrZero(bio?.calories);
    const avgHeartRate = numberOrZero(bio?.averageHeartRate);
    const maxHeartRate = numberOrZero(bio?.maxHeartRate);
    if (calories > 0) { totals.caloriesCount += 1; totals.totalCalories += calories; }
    if (avgHeartRate > 0) { totals.heartRateCount += 1; totals.heartRateSum += avgHeartRate; }
    if (maxHeartRate > 0) { totals.maxHeartRateCount += 1; totals.maxHeartRate = Math.max(totals.maxHeartRate, maxHeartRate); }

    const seen = new Set();
    let workoutVolume = 0;

    for (const exercise of workout.exercises || []) {
      const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
      const name = String(exercise.title || "Unknown exercise").trim() || "Unknown exercise";
      const item = exercises.get(name) || { name, workoutCount: 0, setCount: 0, volumeKg: 0, prSetCount: 0 };

      if (!seen.has(name)) { item.workoutCount += 1; seen.add(name); }
      item.setCount += sets.length;

      const rawMuscles = [exercise.muscleGroup, ...(Array.isArray(exercise.otherMuscles) ? exercise.otherMuscles : [])];

      // Bucket-level counts (for hex radar)
      for (const bucket of exerciseBuckets(exercise)) muscleTotals.set(bucket, (muscleTotals.get(bucket) || 0) + sets.length);

      // Library-muscle-level counts (for body map) — deduplicated per exercise
      const exerciseLibraryMuscles = new Set();
      for (const raw of rawMuscles) {
        const key = normalizeKey(raw);
        const direct = MUSCLE_TO_LIBRARY.get(key);
        if (direct) {
          for (const m of direct) exerciseLibraryMuscles.add(m);
        } else {
          const bucketId = MUSCLE_ALIASES.get(key);
          for (const m of BUCKET_TO_LIBRARY[bucketId] || []) exerciseLibraryMuscles.add(m);
        }
      }
      for (const m of exerciseLibraryMuscles) libraryMuscleTotals.set(m, (libraryMuscleTotals.get(m) || 0) + sets.length);

      for (const set of sets) {
        const vol = calculateSetVolume(set);
        workoutVolume += vol; item.volumeKg += vol;
        totals.totalSets += 1;
        if (set?.isPersonalRecord) { totals.prSetCount += 1; item.prSetCount += 1; }
      }
      const eq = (exercise.equipment || "").trim().toLowerCase() || "other";
      equipmentTotals.set(eq, (equipmentTotals.get(eq) || 0) + sets.length);

      exercises.set(name, item);
    }
    const wVol = workout.metadata?.estimatedVolumeKg ?? workoutVolume;
    totals.totalVolumeKg += wVol;
    if (wVol > 0) totals.volumeWorkoutCount += 1;
  }

  return {
    period: serializePeriod(period),
    workoutCount: totals.workoutCount,
    weeklyWorkoutAverage: totals.workoutCount / (periodDays / 7),
    totalDurationSeconds: totals.totalDurationSeconds,
    averageDurationSeconds: totals.workoutCount ? totals.totalDurationSeconds / totals.workoutCount : 0,
    totalVolumeKg: totals.totalVolumeKg,
    volumeWorkoutCount: totals.volumeWorkoutCount,
    averageVolumeKg: totals.volumeWorkoutCount ? totals.totalVolumeKg / totals.volumeWorkoutCount : 0,
    totalSets: totals.totalSets,
    averageSets: totals.workoutCount ? totals.totalSets / totals.workoutCount : 0,
    prSetCount: totals.prSetCount,
    prRate: totals.totalSets ? totals.prSetCount / totals.totalSets : 0,
    totalCalories: totals.totalCalories,
    averageCalories: totals.caloriesCount ? totals.totalCalories / totals.caloriesCount : 0,
    caloriesWorkoutCount: totals.caloriesCount,
    averageHeartRate: totals.heartRateCount ? totals.heartRateSum / totals.heartRateCount : 0,
    heartRateWorkoutCount: totals.heartRateCount,
    maxHeartRate: totals.maxHeartRateCount ? totals.maxHeartRate : 0,
    maxHeartRateWorkoutCount: totals.maxHeartRateCount,
    muscleBuckets: MUSCLE_BUCKETS.map((b) => ({ ...b, setCount: muscleTotals.get(b.id) || 0 })),
    muscleLibraryCounts: [...libraryMuscleTotals.entries()].map(([muscle, setCount]) => ({ muscle, setCount })),
    exerciseFrequency: [...exercises.values()].sort((a, b) =>
      b.volumeKg - a.volumeKg || b.setCount - a.setCount || a.name.localeCompare(b.name)),
    equipmentBreakdown: [...equipmentTotals.entries()]
      .map(([id, setCount]) => ({ id, setCount }))
      .sort((a, b) => b.setCount - a.setCount)
  };
}

function buildDeltas(current, previous) {
  const keys = ["workoutCount", "weeklyWorkoutAverage", "totalDurationSeconds", "averageDurationSeconds",
    "totalVolumeKg", "totalSets", "averageSets", "prSetCount", "prRate",
    "totalCalories", "averageHeartRate"];
  return Object.fromEntries(keys.map((key) => [key, compareValues(current[key], previous[key])]));
}

export function compareValues(current, previous) {
  const c = numberOrZero(current), p = numberOrZero(previous);
  const absolute = c - p;
  const percent = p ? absolute / Math.abs(p) : c ? null : 0;
  return { current: c, previous: p, absolute, percent, direction: absolute > 0 ? "up" : absolute < 0 ? "down" : "flat" };
}

// Which of N profiles leads a metric, and by how much over the runner-up.
// `null` when the top value is shared — with four profiles a tie for the lead
// is common enough that "nobody leads" has to be a real answer. The percentage
// is the same one compareValues() reports: the gap relative to the value below.
export function leadAmong(values) {
  const numbers = values.map(numberOrZero);
  if (numbers.length < 2) return null;

  const best = Math.max(...numbers);
  if (numbers.filter((value) => value === best).length > 1) return null;

  const index = numbers.indexOf(best);
  const runnerUp = Math.max(...numbers.filter((_, i) => i !== index));
  return { index, percent: runnerUp ? (best - runnerUp) / Math.abs(runnerUp) : null };
}

// The six dashboard buckets an exercise touches, deduplicated: an exercise that
// hits chest and shoulders adds its sets to both, but only once each.
function exerciseBuckets(exercise) {
  const raw = [exercise.muscleGroup, ...(Array.isArray(exercise.otherMuscles) ? exercise.otherMuscles : [])];
  return [...new Set(raw.map(mapMuscleToBucket).filter(Boolean))];
}

function countWeekMuscleSets(workouts) {
  const totals = new Map();
  for (const workout of workouts) {
    for (const exercise of workout.exercises || []) {
      const setCount = Array.isArray(exercise.sets) ? exercise.sets.length : 0;
      if (!setCount) continue;
      for (const bucket of exerciseBuckets(exercise)) totals.set(bucket, (totals.get(bucket) || 0) + setCount);
    }
  }
  return Object.fromEntries(MUSCLE_BUCKETS.map((b) => [b.id, totals.get(b.id) || 0]));
}

function buildWeeklySeries(workouts, start, end) {
  const rows = [];
  for (let cursor = getUtcWeekStart(start); cursor < end; cursor = addUtcDays(cursor, 7)) {
    const weekEnd = addUtcDays(cursor, 7);
    const ww = workouts.filter((w) => w._date >= cursor && w._date < weekEnd);
    rows.push({
      date: toIsoDate(cursor),
      label: formatShortDate(cursor),
      workoutCount: ww.length,
      totalDurationSeconds: sum(ww, "durationSeconds"),
      // Bucket sums can exceed the week's raw set count — a set counts once per
      // muscle the exercise trains. Chart these against each other, not totals.
      muscleSets: countWeekMuscleSets(ww)
    });
  }
  return rows;
}

function buildMonthlySeries(workouts, start, end) {
  const rows = [];
  let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  while (cursor < end) {
    const next = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    const mw = workouts.filter((w) => w._date >= cursor && w._date < next);
    rows.push({ date: toIsoDate(cursor), label: new Intl.DateTimeFormat("en-US", { month: "short" }).format(cursor), workoutCount: mw.length, totalDurationSeconds: sum(mw, "durationSeconds") });
    cursor = next;
  }
  return rows;
}

// Plateau detection. Deliberately conservative: the import is capped, so a
// thin history would otherwise let us call a plateau on three sessions in one
// week. An exercise needs enough sessions AND enough calendar time before we
// say anything at all.
const TREND_SESSIONS = 6;
const TREND_MIN_SPAN_DAYS = 14;
// Session-to-session estimates are noisy; only a move outside this band counts.
const TREND_BAND = 0.02;

// Epley. Past ~12 reps the estimate drifts high, so the rep bonus is capped
// rather than letting a 20-rep back-off set outrank a heavy triple.
export function estimateOneRepMax(set) {
  const weight = Number(set?.weight);
  const reps = Number(set?.reps);
  if (!Number.isFinite(weight) || !Number.isFinite(reps) || weight <= 0 || reps <= 0) return 0;
  return weight * (1 + Math.min(reps, 12) / 30);
}

function mean(values) {
  return values.reduce((total, value) => total + value, 0) / values.length;
}

// Per exercise: the best estimated 1RM of each session, then the recent half
// against the half before it. Bodyweight work never produces a positive
// estimate, so it simply never reports a trend.
function buildExerciseTrends(workouts) {
  const sessions = new Map();

  for (const workout of workouts) {
    for (const exercise of workout.exercises || []) {
      const name = String(exercise.title || "Unknown exercise").trim() || "Unknown exercise";
      let best = 0;
      for (const set of Array.isArray(exercise.sets) ? exercise.sets : []) {
        const estimate = estimateOneRepMax(set);
        if (estimate > best) best = estimate;
      }
      if (!best) continue;
      const rows = sessions.get(name) || [];
      rows.push({ date: workout._date, value: best });
      sessions.set(name, rows);
    }
  }

  const trends = new Map();
  const half = TREND_SESSIONS / 2;

  for (const [name, rows] of sessions) {
    if (rows.length < TREND_SESSIONS) continue;
    rows.sort((a, b) => a.date - b.date);

    const recent = rows.slice(-TREND_SESSIONS);
    const spanDays = (recent[recent.length - 1].date - recent[0].date) / (24 * 60 * 60 * 1000);
    if (spanDays < TREND_MIN_SPAN_DAYS) continue;

    const older = mean(recent.slice(0, half).map((r) => r.value));
    const newer = mean(recent.slice(half).map((r) => r.value));
    if (!older) continue;

    const change = (newer - older) / older;
    const status = change > TREND_BAND ? "progressing" : change < -TREND_BAND ? "declining" : "plateau";

    trends.set(name, {
      status,
      change,
      sessions: recent.length,
      spanDays: Math.round(spanDays)
    });
  }

  return trends;
}

const CONSISTENCY_MAX_WEEKS = 53;

// A GitHub-style day grid plus week streaks. The window ends on the current week
// and reaches back at most a year — but never past the oldest imported workout,
// because the import is capped and empty cells before it would read as "trained
// nothing" when the truth is "we never fetched it".
function buildConsistency(workouts, nowInput) {
  const now = parseDate(nowInput) || new Date();
  const today = toUtcMidnight(now);
  const lastWeekStart = getUtcWeekStart(today);

  if (!workouts.length) {
    return { weeks: 0, days: [], activeDays: 0, activeWeeks: 0, currentStreakWeeks: 0, longestStreakWeeks: 0, maxCount: 0, windowStart: null, clampedToFirstWorkout: false };
  }

  const counts = new Map();
  let oldest = null;
  for (const workout of workouts) {
    const day = toUtcMidnight(workout._date);
    if (!oldest || day < oldest) oldest = day;
    const key = toIsoDate(day);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  const yearStart = addUtcDays(lastWeekStart, -7 * (CONSISTENCY_MAX_WEEKS - 1));
  const oldestWeekStart = getUtcWeekStart(oldest);
  const clampedToFirstWorkout = oldestWeekStart > yearStart;
  const windowStart = clampedToFirstWorkout ? oldestWeekStart : yearStart;
  const weeks = Math.round((lastWeekStart - windowStart) / (7 * 24 * 60 * 60 * 1000)) + 1;

  const days = [];
  const weekActive = [];
  let activeDays = 0;
  let maxCount = 0;

  for (let week = 0; week < weeks; week += 1) {
    const weekStart = addUtcDays(windowStart, week * 7);
    let active = false;
    for (let weekday = 0; weekday < 7; weekday += 1) {
      const day = addUtcDays(weekStart, weekday);
      // Days after today are placeholders in the current column, not zeroes.
      const count = day > today ? null : counts.get(toIsoDate(day)) || 0;
      if (count) {
        active = true;
        activeDays += 1;
        if (count > maxCount) maxCount = count;
      }
      days.push({ date: toIsoDate(day), count, week, weekday });
    }
    weekActive.push(active);
  }

  let longestStreakWeeks = 0;
  let run = 0;
  for (const active of weekActive) {
    run = active ? run + 1 : 0;
    if (run > longestStreakWeeks) longestStreakWeeks = run;
  }

  // The current week is still in progress, so an empty one does not break a
  // streak yet — start counting from the last completed week instead.
  let cursor = weekActive.length - 1;
  if (!weekActive[cursor]) cursor -= 1;
  let currentStreakWeeks = 0;
  while (cursor >= 0 && weekActive[cursor]) {
    currentStreakWeeks += 1;
    cursor -= 1;
  }

  return {
    weeks,
    days,
    activeDays,
    activeWeeks: weekActive.filter(Boolean).length,
    currentStreakWeeks,
    longestStreakWeeks,
    maxCount,
    windowStart: toIsoDate(windowStart),
    clampedToFirstWorkout
  };
}

function toUtcMidnight(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function getUtcWeekStart(date) {
  const v = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  v.setUTCDate(v.getUTCDate() - (v.getUTCDay() || 7) + 1);
  return v;
}

function normalizeKey(value) {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function parseDate(value) {
  const d = value instanceof Date ? new Date(value) : new Date(String(value || ""));
  return Number.isNaN(d.valueOf()) ? null : d;
}

function addUtcDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function toIsoDate(date) { return date.toISOString().slice(0, 10); }
function formatShortDate(date) { return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date); }
function sum(rows, key) { return rows.reduce((t, r) => t + numberOrZero(r[key]), 0); }
function numberOrZero(value) { const n = Number(value); return Number.isFinite(n) ? n : 0; }
